import { Link } from 'react-router-dom'

import { Badge } from './ui.tsx'
import { ago, duration, odds } from '../lib/format.ts'
import { displayRunner } from '../../shared/runners.ts'
import type { MarketSignal } from '../../shared/types.ts'

/**
 * How a price move is named and coloured, in one place, so the rail, the row badge, the
 * market page and Home all say the same thing.
 *
 * Direction is carried by the word and the arrow, never by colour alone: steam is a
 * price that shortened, drift one that lengthened, and neither is good or bad on its
 * own — that depends on which side you are.
 */
export function signalLabel(kind: MarketSignal['kind']): { word: string; arrow: string; tone: string } {
  return kind === 'steam' ? { word: 'Steam', arrow: '▼', tone: 'teal' } : { word: 'Drift', arrow: '▲', tone: 'warn' }
}

export function SignalBadge({ kind, pct }: { kind: MarketSignal['kind']; pct: number }) {
  const l = signalLabel(kind)
  return (
    <Badge tone={l.tone}>
      {l.arrow} {l.word} {formatPct(pct)}
    </Badge>
  )
}

/** Signed to one decimal, with the sign always shown: a move has a direction. */
export function formatPct(pct: number): string {
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`
}

/**
 * One live signal as a compact card: what moved, by how much, over how long, and a way
 * to the market. Used side by side in the "Moving now" rail and stacked on Home.
 */
export function SignalCard({
  signal: s,
  showEvent = true,
  grow = false,
}: {
  signal: MarketSignal
  showEvent?: boolean
  /** Share a row with its neighbours (the rail) rather than sit in a column (Home). */
  grow?: boolean
}) {
  const detectedS = (Date.now() - new Date(s.detectedAt).getTime()) / 1000
  return (
    <Link
      to={`/markets/${s.marketId}`}
      className="card"
      style={{ display: 'block', padding: '10px 12px', minWidth: 230, flex: grow ? '1 1 230px' : undefined, textDecoration: 'none' }}
    >
      <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
        <span className="cell-strong">{displayRunner(s.runner)}</span>
        <SignalBadge kind={s.kind} pct={s.pct} />
      </div>
      <div className="u-mono" style={{ marginTop: 4 }}>
        {odds(s.fromPrice)} → {odds(s.toPrice)}{' '}
        <span className="cell-sub">
          {Math.abs(s.ticks)} tick{Math.abs(s.ticks) === 1 ? '' : 's'} in {duration(s.windowS)}
        </span>
      </div>
      {showEvent && (
        <div className="cell-sub" style={{ marginTop: 4 }}>
          {s.event} · {s.marketType}
          {s.inPlay ? ' · in-play' : ''} · {ago(detectedS)}
        </div>
      )}
    </Link>
  )
}
